import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Iconfont from "@components/iconfont/iconfont";
import "./landing.less";
import PricePage from "./price/PricePage";
import MobileNewsPage from "./news/MobileNewsPage";
import MobileNewsDetail from "./news/MobileNewsDetail";
import useWebsiteNavigation from "../shared/useWebsiteNavigation";
import useNewsEntryVisible from "../shared/useNewsEntryVisible";
import { scrollPageToTop } from "../shared/scrollTop";

import {
  IMG,
  CASE_BG,
  GENERATED_PORTFOLIO_IMGS,
  PRIVATE_SPHERE_IMGS,
  SCROLL_BRAND_IMAGES,
} from "../shared/image_assets";
import { linkData } from "../../official_website/data";
import { protocol } from "@util/enum";
import { getCookie, getUrl, identifyDevicePlatform, downloadFile } from "@util/utils";
import { queryUserByToken } from "@api/user";
import { navigateToPrivateDomain } from "../shared/privateDomainNav";
import { QRCodeCanvas } from "qrcode.react";

/** 统一别名：JSX 中只引用这些点位的资源；CASE_IMAGES 沿用本文件原名（PC 端用的是 CASE_BG） */
const imgHeroScreen = IMG.heroScreen;
const imgHeroCard1 = IMG.heroCard1;
const imgHeroCard2 = IMG.heroCard2;
const imgHeroCard3 = IMG.heroCard3;
const imgHeroCard4 = IMG.heroCard4;
const imgCreateWeb = IMG.createWeb;
const imgXiaochengxu = IMG.xiaochengxu;
const imgMiniProgramBg = IMG.miniProgramBg;
const imgDemoCursor = IMG.demoCursor; // 本地 shubiao.svg
const imgBujueLogo = IMG.bujueLogo;
const imgBujueLogoWhite = IMG.bujueLogoWhite;
const imgWxKf = IMG.wxKf;
const iconArrowRight = IMG.arrowRight;

function resolveImportedAsset(imported) {
  if (imported == null) return "";
  if (typeof imported === "string") return imported;
  if (typeof imported === "object") {
    if (typeof imported.src === "string") return imported.src;
    const d = imported.default;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && typeof d.src === "string") return d.src;
  }
  return "";
}
const assetUrl = resolveImportedAsset;
const bgImageStyle = (i) => {
  const u = assetUrl(i);
  return u ? { backgroundImage: `url("${u}")` } : {};
};

/** 操作说明：现网「不觉 SAAS 私域」帮助页（tabIndex=3） */
const OFFICIAL_SITE_INSTRUCTIONS_URL =
  "https://bujue.art/international.html#/share_page?clientId=66e1042ce932c875f8884314&tabIndex=3";
/** 关于我们：直接外链至不觉国际站 share_page tab=1 */
const OFFICIAL_SITE_ABOUT_US_URL =
  "https://bujue.at.bujue.art/international.html#/share_page?clientId=66e1042ce932c875f8884314&tabIndex=1";
/** 下载介绍：不觉中文介绍 PDF，直接下载 */
const OFFICIAL_SITE_INTRO_PDF_URL =
  "https://bujue-static-cdn.bujue.art/%E5%AE%98%E7%BD%91%E8%B5%84%E6%BA%90/%E4%B8%8D%E8%A7%89%20bujue.art%20%E4%B8%AD%E6%96%87%E4%BB%8B%E7%BB%8D.pdf";

const CASE_IMAGES = CASE_BG;

const NAV_ITEMS = [
  {
    key: "product",
    label: "产品",
    children: [
      { label: "官网搭建", href: "#m-product-create-web" },
      { label: "生成作品集/产品册", href: "#m-product-portfolio" },
      { label: "商业伙伴圈", href: "#m-product-partners" },
      { label: "微信小程序", href: "#m-product-mini-program" },
    ],
  },
  { key: "cases", label: "案例", href: "#m-cases" },
  { key: "news", label: "资讯" },
  { key: "price", label: "价格" },
  { key: "help", label: "帮助", href: OFFICIAL_SITE_INSTRUCTIONS_URL, external: true },
];

const PRIVATE_SPHERE_MAX_STEP = 2;

const SERVICES = [
  {
    id: "m-product-create-web",
    title: "零代码搭建品牌（个人/企业）网站",
    desc: [
      "无需一行代码，个性化搭建品牌官网",
      "视觉美学精彩呈现，支持多语言助力全球出海",
      "电脑与手机自适应，快速上线品牌官网",
    ],
    img: imgCreateWeb,
    imgs: [imgCreateWeb],
  },
  {
    id: "m-product-portfolio",
    title: "生成高定作品集/产品册",
    desc: [
      "上传即拥有数据化、结构化的美学作品/产品库",
      "链接可分享，避免图片的繁杂制作和庞大文件的空间占用",
      "无需手动转版，自动生成美学版式，可自由分类",
      "自动设置分享内容，分享全程可追踪",
      "多语言支持，自动适配电脑、Pad、手机、大屏等端口",
    ],
    img: IMG.generated1,
    imgs: GENERATED_PORTFOLIO_IMGS,
    stepMax: 3,
  },
  {
    id: "m-product-partners",
    title: "商业圈-商业伙伴圈",
    desc: [
      "私域间引流：可与自己建联的商业伙伴圈，相互实现品牌、产品曝光"
    ],
    img: IMG.private1,
    imgs: PRIVATE_SPHERE_IMGS,
    stepMax: PRIVATE_SPHERE_MAX_STEP,
  },
  {
    id: "m-product-mini-program",
    title: "快速生成品牌微信小程序",
    desc: [
      "无需代码，为你快速生成专属品牌、精美视觉的微信小程序",
      "一步跨越技术门槛，轻量抢占移动端入口。",
    ],
    img: imgXiaochengxu,
    imgs: [imgXiaochengxu],
  },
];

export default function MobileApp() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedNav, setExpandedNav] = useState("product");
  /** 当前视图：landing（首页全部 section）/ price（价格页）/ news（资讯页） */
  const { view, newsDetailId, newsDetailTitle, navigate } = useWebsiteNavigation();
  /** 抽屉顶部「资讯」入口：分类接口有数据才展示 */
  const newsEntryVisible = useNewsEntryVisible();
  /** 资讯列表是否还有下一页没加载完（由 MobileNewsPage 上报，用于先藏 footer） */
  const [newsFooterHidden, setNewsFooterHidden] = useState(false);
  const [caseActive, setCaseActive] = useState(0);
  const [contactVisible, setContactVisible] = useState(false);
  const [appModalVisible, setAppModalVisible] = useState(false);
  const [genStep, setGenStep] = useState(0);
  const [psStep, setPsStep] = useState(0);
  /** 小程序模块：0 第一视觉 / 1 第二视觉。仅用于 background-size contain↔cover 这种
   *  不可插值属性的二态切换；细粒度位移/不透明度由 CSS var --mp-progress 直接驱动。 */
  const [miniProgramStage, setMiniProgramStage] = useState(0);
  const navRef = useRef(null);
  const rootRef = useRef(null);
  const portfolioRef = useRef(null);
  const privateSphereRef = useRef(null);
  const miniProgramRef = useRef(null);
  const genStepRef = useRef(0);
  const psStepRef = useRef(0);
  const miniProgramStageRef = useRef(0);

  const handleIntroDownload = useCallback(() => {
    downloadFile(OFFICIAL_SITE_INTRO_PDF_URL, "不觉bujue.art中文介绍.pdf");
  }, []);

  useEffect(() => {
    document.title = "不觉 — 0 代码搭建品牌网站 · 私域交易工具";
  }, []);

  /** 抽屉打开时禁止 body 滚动 */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = drawerOpen ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [drawerOpen]);

  /** nav 滚动透明度过渡 */
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const onScroll = () => {
      if (window.scrollY > 20) nav.classList.add("m-nav--scrolled");
      else nav.classList.remove("m-nav--scrolled");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /** 卡片 reveal：进入视口加 .in，离开视口移除——上下滚动都会再次触发。
   *  添加 .in 时双 rAF 延迟一帧，保证初始 opacity/transform/filter 先被绘制，
   *  否则首屏即相交的卡片会跳过过渡直接呈现终态。
   *  依赖 [view]：view 切回 landing 时 card 是新挂载的 DOM，必须重建 IO 让新元素被观察，
   *  否则 IO 还指向已销毁的旧节点，新 card 永远不会进入 reveal 状态。 */
  useEffect(() => {
    if (view !== "landing") return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const el = e.target;
          if (e.isIntersecting) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => el.classList.add("in"));
            });
          } else {
            el.classList.remove("in");
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    root.querySelectorAll(".m-service__card").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [view]);

  /** 作品集 / 私域伙伴圈 + 小程序：滚动驱动图片切换与进度 */
  useEffect(() => {
    let raf = 0;

    const progressOf = (el) => {
      if (!el) return -1;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const midY = (r.top + r.bottom) / 2;
      const start = vh * 0.85;
      const end = vh * 0.15;
      const raw = (start - midY) / (start - end);
      return raw < 0 ? 0 : raw > 1 ? 1 : raw;
    };

    const evaluate = () => {
      const gp = progressOf(portfolioRef.current);
      if (gp >= 0) {
        const next = Math.min(3, Math.max(0, Math.floor(gp * 4)));
        if (next !== genStepRef.current) {
          genStepRef.current = next;
          setGenStep(next);
        }
      }
      const pp = progressOf(privateSphereRef.current);
      if (pp >= 0) {
        const next = Math.min(
          PRIVATE_SPHERE_MAX_STEP,
          Math.max(0, Math.floor(pp * (PRIVATE_SPHERE_MAX_STEP + 1)))
        );
        if (next !== psStepRef.current) {
          psStepRef.current = next;
          setPsStep(next);
        }
      }
      const mpEl = miniProgramRef.current;
      if (mpEl) {
        const r = mpEl.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        const midY = (r.top + r.bottom) / 2;
        const start = vh * 0.89;
        const end = vh * 0.35;
        const raw = (start - midY) / (start - end);
        const p = raw < 0 ? 0 : raw > 1 ? 1 : raw;
        mpEl.style.setProperty("--mp-progress", p.toFixed(3));
        const nextStage = p > 0.5 ? 1 : 0;
        if (miniProgramStageRef.current !== nextStage) {
          miniProgramStageRef.current = nextStage;
          setMiniProgramStage(nextStage);
        }
      }
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        evaluate();
      });
    };

    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  const handlePrivateDomainClick = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setDrawerOpen(false);
    navigateToPrivateDomain({
      platform: identifyDevicePlatform(),
      isWx: Boolean(navigator.userAgent.match(/micromessenger/i)),
      isDd: /DingTalk/.test(navigator.userAgent),
      token: getCookie("mtoken"),
    });
  }, []);

  const handleNavLink = useCallback((href, external) => {
    setDrawerOpen(false);
    if (!href || href === "#") return;
    if (external) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    /** landing 内锚点跳转：跨 view 时先切 landing，等 useEffect 注册完 IO/scroll handler，
     *  再用 instant 跳转（避免 smooth 滚动经过 m-service 时 progressOf 拿到旧 layout 把 transform 卡死）。
     *  跳转完主动给当前视口内的 m-service__card 补 `.in`，兜底 IO 双 rAF 与瞬时滚动的时序竞争。 */
    if (view !== "landing") {
      navigate("landing");
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
        requestAnimationFrame(() => requestAnimationFrame(() => {
          /** 跨 view 跳转后，IO 的双 rAF 与 progressOf scroll handler 重渲存在时序竞争；
           *  直接给所有 card 强加 `.in`，让用户立刻看到完整内容（跳过 reveal 动画）。 */
          document.querySelectorAll(".m-service__card").forEach((card) => {
            card.classList.add("in");
          });
          window.dispatchEvent(new Event("scroll"));
        }));
      }, 60);
      return;
    }
    requestAnimationFrame(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [view, navigate]);

  const handleGoPrice = useCallback(() => {
    setDrawerOpen(false);
    navigate("price");
    scrollPageToTop();
  }, [navigate]);

  const handleGoNews = useCallback(() => {
    setDrawerOpen(false);
    navigate("news");
    scrollPageToTop();
  }, [navigate]);

  /** 打开资讯详情并记录历史，view 保持 news，顶部 nav 不变。 */
  const handleOpenNewsDetail = useCallback((id, title) => {
    if (!id) return;
    navigate("news", id, title || "");
    scrollPageToTop();
  }, [navigate]);

  const handleBackToNewsList = useCallback(() => {
    navigate("news");
    scrollPageToTop();
  }, [navigate]);

  /**
   * 资讯切换（列表 ⇄ 详情、详情之间）渲染完成后再置顶一次。
   *
   * 点击处理函数里的置顶发生在 React 替换正文之前；iOS Safari 在随后的文档高度变化
   * （旧正文卸载 → 加载态变矮 → 新正文变高）时会丢掉这次滚动，表现为停在半路。
   * useLayoutEffect 在 DOM 提交后、浏览器绘制前执行，此时新内容已经落地，置顶不会再被打断。
   * 下一帧再检查一次：iOS 偶发在点击手势的同一任务里忽略程序化滚动，只有确实没到顶才补。
   */
  useLayoutEffect(() => {
    if (view !== "news") return undefined;
    scrollPageToTop();
    const raf = window.requestAnimationFrame(() => {
      if (window.scrollY > 0) scrollPageToTop();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [view, newsDetailId]);

  /** 抽屉一级项分发：价格 / 资讯 切视图，其余走锚点或外链 */
  const handleDrawerItem = useCallback(
    (item) => {
      if (item.key === "price") return handleGoPrice();
      if (item.key === "news") return handleGoNews();
      return handleNavLink(item.href, item.external);
    },
    [handleGoPrice, handleGoNews, handleNavLink]
  );

  const handleGoLanding = useCallback((e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setDrawerOpen(false);
    navigate("landing");
    scrollPageToTop();
  }, [navigate]);

  const totalCases = CASE_IMAGES.length;
  const caseTimerRef = useRef(null);
  const touchStartXRef = useRef(null);

  const restartCaseTimer = useCallback(() => {
    if (caseTimerRef.current) window.clearInterval(caseTimerRef.current);
    caseTimerRef.current = window.setInterval(
      () => setCaseActive((a) => (a + 1) % totalCases),
      4000
    );
  }, [totalCases]);

  const goCase = useCallback(
    (i) => {
      setCaseActive((i + totalCases) % totalCases);
      restartCaseTimer();
    },
    [totalCases, restartCaseTimer]
  );

  useEffect(() => {
    restartCaseTimer();
    return () => {
      if (caseTimerRef.current) window.clearInterval(caseTimerRef.current);
    };
  }, [restartCaseTimer]);

  const handleCaseTouchStart = useCallback((e) => {
    if (caseTimerRef.current) window.clearInterval(caseTimerRef.current);
    touchStartXRef.current = e.touches[0].clientX;
  }, []);

  const handleCaseTouchEnd = useCallback(
    (e) => {
      const dx =
        (e.changedTouches[0].clientX - (touchStartXRef.current || 0));
      if (Math.abs(dx) > 40) {
        setCaseActive((a) =>
          dx > 0
            ? (a - 1 + totalCases) % totalCases
            : (a + 1) % totalCases
        );
      }
      restartCaseTimer();
    },
    [totalCases, restartCaseTimer]
  );

  const handleAppDownload = useCallback(() => {
    if (/Mobi|Android|iPhone/i.test(navigator.userAgent)) {
      window.open("https://bujue.art/down_app.html");
    } else {
      setAppModalVisible(true);
    }
  }, []);

  /** 同 PC：cookie 跨子域共享，必须用 queryUserByToken 二次校验 */
  const [hasAuthCookie, setHasAuthCookie] = useState(false);

  useEffect(() => {
    const token = getCookie("mtoken");
    if (!token) {
      setHasAuthCookie(false);
      return;
    }
    let cancelled = false;
    queryUserByToken(token)
      .then((res) => {
        if (cancelled) return;
        let user = null;
        try {
          user = typeof res === "string" ? JSON.parse(res) : res;
        } catch (_) {
          user = null;
        }
        setHasAuthCookie(Boolean(user && (user.id || user.userId || user.uid)));
      })
      .catch(() => {
        if (!cancelled) setHasAuthCookie(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* 资讯列表还有更多 / 还在加载时先藏掉 footer：这让文档底部正好等于列表底部，
     「到底后再下拉一段才加载下一页」的手势才能稳定接管；全部加载完 footer 再随页面滚出。
     详情页 / 空态 / 错误态都不会上报隐藏，footer 正常展示。 */
  const hideFooter = view === "news" && !newsDetailId && newsFooterHidden;

  return (
    <>
    <div
      className={`m-landing${view === "news" ? " m-landing--news" : ""}`}
      ref={rootRef}
    >
      <header ref={navRef} className="m-nav">
        <a href="#" className="m-nav__brand" onClick={handleGoLanding}>
          <img src={assetUrl(imgBujueLogo)} alt="不觉" />
        </a>
        <div className="m-nav__actions">
          <a
            href="#"
            className="m-nav__cta"
            onClick={handlePrivateDomainClick}
          >
            获取APP
          </a>
          <button
            type="button"
            className={`m-nav__hamburger${drawerOpen ? " is-open" : ""}`}
            aria-label="菜单"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((v) => !v)}
          >
            <Iconfont type="icon-a-caidanxi" />
          </button>
        </div>
      </header>

      <div
        className={`m-drawer${drawerOpen ? " is-open" : ""}`}
        aria-hidden={!drawerOpen}
      >
        <div className="m-drawer__head">
          <a href="#" className="m-nav__brand" onClick={handleGoLanding}>
            <img src={assetUrl(imgBujueLogo)} alt="不觉" />
          </a>
          <button
            type="button"
            className="m-drawer__close"
            aria-label="关闭"
            onClick={() => setDrawerOpen(false)}
          >
            <Iconfont type="icon-a-guanbixi" />
          </button>
        </div>
        <nav className="m-drawer__list">
          {NAV_ITEMS.filter(
            (item) => item.key !== "news" || newsEntryVisible
          ).map((item) =>
            item.children ? (
              <div key={item.key} className="m-drawer__group">
                <button
                  type="button"
                  className={`m-drawer__row m-drawer__row--toggle${
                    expandedNav === item.key ? " is-expanded" : ""
                  }`}
                  onClick={() => {
                    /** 从价格页 / 资讯页点产品父级：切回 landing 顶部，再展开子菜单（让用户用得到的返回入口）。
                     *  这里必须是同步置顶：html 上有 scroll-behavior: smooth，behavior: "auto"
                     *  同样会被平滑动画接管，而随后的视图切换会打断它。 */
                    if (view !== "landing") {
                      navigate("landing");
                      scrollPageToTop();
                    }
                    setExpandedNav((cur) => (cur === item.key ? null : item.key));
                  }}
                >
                  <span>{item.label}</span>
                  <img src={assetUrl(iconArrowRight)} className="m-drawer__chevron" alt="" aria-hidden />
                </button>
                {expandedNav === item.key && (
                  <div className="m-drawer__sublist">
                    {item.children.map((c) => (
                      <button
                        key={c.label}
                        type="button"
                        className="m-drawer__row m-drawer__row--sub"
                        onClick={() => handleNavLink(c.href)}
                      >
                        <span>{c.label}</span>
                        <img src={assetUrl(iconArrowRight)} className="m-drawer__chevron" alt="" aria-hidden />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                key={item.key}
                type="button"
                className="m-drawer__row"
                onClick={() => handleDrawerItem(item)}
              >
                <span>{item.label}</span>
                <img src={assetUrl(iconArrowRight)} className="m-drawer__chevron" alt="" aria-hidden />
              </button>
            )
          )}
        </nav>
        <div className="m-drawer__cta">
          <a
            href="#"
            className="m-btn m-btn--primary m-btn--block"
            onClick={handlePrivateDomainClick}
          >
            进入私域
          </a>
        </div>
      </div>
      {drawerOpen && (
        <div
          className="m-drawer-mask"
          aria-hidden
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {view === "price" && (
        <PricePage
          onContact={() => setContactVisible(true)}
          onSubscribe={handlePrivateDomainClick}
        />
      )}

      {/* 资讯：列表 / 详情共用 view=news，顶部 nav / footer 常驻 */}
      {view === "news" &&
        (newsDetailId ? (
          <MobileNewsDetail
            id={newsDetailId}
            title={newsDetailTitle}
            onBack={handleBackToNewsList}
            onOpenDetail={handleOpenNewsDetail}
          />
        ) : (
          <MobileNewsPage
            onOpenDetail={handleOpenNewsDetail}
            onFooterHiddenChange={setNewsFooterHidden}
          />
        ))}

      {view === "landing" && (
      <>
      <section className="m-hero">
        <h1 className="m-hero__title">
          {/* <span>私域圈新商业工具</span> */}
          <span>让每一个创作者、每一个企业</span>
          <span>都有自己的品牌阵地</span>
        </h1>
        <div className="m-hero__tags">
          <span>零代码搭建品牌网站</span>
          <span>作品集/产品册</span>
          <span>私域新商业工具</span>
        </div>
        <div className="m-hero__cta">
          <a
            href="#"
            className="m-btn m-btn--primary"
            onClick={handlePrivateDomainClick}
          >
            立即使用
          </a>
          <a
            href="https://v.wjx.cn/vm/PD6aaEY.aspx#"
            className="m-btn m-btn--primary m-btn--white"
            target="_blank"
            rel="noopener noreferrer"
          >
            联系我们
          </a>
        </div>
        <div className="m-hero__stage">
          <div className="m-hero__panel" aria-hidden />
          <div className="m-hero__device" style={bgImageStyle(imgHeroScreen)} />
          <div
            className="m-hero__pop m-hero__pop--tl"
            style={bgImageStyle(imgHeroCard1)}
          />
          <div
            className="m-hero__pop m-hero__pop--tr"
            style={bgImageStyle(imgHeroCard2)}
          />
          <div
            className="m-hero__pop m-hero__pop--bl"
            style={bgImageStyle(imgHeroCard4)}
          />
          <div
            className="m-hero__pop m-hero__pop--br"
            style={bgImageStyle(imgHeroCard3)}
          />
        </div>
      </section>

      <section className="m-service" id="m-service">
        <div className="m-sec-head">
          <span className="m-eyebrow">不觉功能</span>
          <div className="m-sec-title">不觉做什么</div>
          <p>专注服务于设计、家居建材及泛美学行业</p>
        </div>
        <div className="m-service__list">
          {SERVICES.map((s) => {
            const isCreateWeb = s.id === "m-product-create-web";
            const isPortfolio = s.id === "m-product-portfolio";
            const isPartners = s.id === "m-product-partners";
            const isMiniProgram = s.id === "m-product-mini-program";
            const isSlides = isPortfolio || isPartners;

            let cardRef = null;
            if (isPortfolio) cardRef = portfolioRef;
            else if (isPartners) cardRef = privateSphereRef;
            else if (isMiniProgram) cardRef = miniProgramRef;

            /* slides 图片索引（横向滑入/滑出） */
            let slideActive = 0;
            if (isPortfolio) slideActive = genStep >= 3 ? 2 : genStep;
            else if (isPartners) slideActive = psStep >= PRIVATE_SPHERE_MAX_STEP ? 1 : psStep;

            /* 注意：stage 类不能放到 article 上——IntersectionObserver 通过
             *  classList.add('in') 直接写 DOM，而 React 在 stage 改变时会重渲整段
             *  className 把 .in 覆盖掉，造成整张卡片回到 .reveal { opacity:0 } 一片空白。
             *  改放到 media 子元素上。 */
            const cardCls = [
              "m-service__card",
              "reveal",
              isCreateWeb ? "m-service__card--wad" : "",
              isSlides ? "m-service__card--slides" : "",
              isMiniProgram ? "m-service__card--mp" : "",
            ]
              .filter(Boolean)
              .join(" ");

            const fxActive = isPartners
              ? (psStep < PRIVATE_SPHERE_MAX_STEP ? psStep : -1)
              : isPortfolio
              ? (genStep >= 3 ? -1 : genStep >= 2 ? 2 : genStep)
              : -1;

            return (
              <article
                key={s.id}
                className={cardCls}
                id={s.id}
                ref={cardRef}
              >
                {/* ── 小程序：phone mock ── */}
                {isMiniProgram && (
                  <div
                    className={`m-service__media m-service__media--mp${
                      miniProgramStage === 1 ? " m-mini-program-stage--alt" : ""
                    }`}
                  >
                    <div
                      className="m-phone-mock__bg"
                      style={bgImageStyle(imgMiniProgramBg)}
                      aria-hidden
                    />
                    <div className="m-phone-mock__overlay" aria-hidden />
                    <div className="m-phone-mock__devices" aria-hidden="true">
                      <div className="m-phone-device m-phone-device--left">
                        <div className="m-phone-screen">
                          <img
                            className="m-phone-screen__img"
                            src={assetUrl(imgXiaochengxu)}
                            alt=""
                            draggable={false}
                          />
                        </div>
                      </div>
                      <div className="m-phone-device m-phone-device--right">
                        <div className="m-phone-screen">
                          <img
                            className="m-phone-screen__img"
                            src={assetUrl(imgXiaochengxu)}
                            alt=""
                            draggable={false}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── 建站：WAD 叠加层 ── */}
                {isCreateWeb && (
                  <div className="m-service__media m-service__media--wad">
                    <img
                      className="m-wad__img"
                      src={assetUrl(s.img)}
                      alt=""
                    />
                    <div className="m-wad__copy">
                      <p className="m-wad__est">
                        EST. CHINA · GLOBAL
                      </p>
                      <div className="m-wad__content">
                        <p className="m-wad__intro">— INTRODUCTION</p>
                        <h4 className="m-wad__title">WAD</h4>
                        <p className="m-wad__sub-zh">
                          中国首家豪宅设计建造顾问企业
                        </p>
                        <p className="m-wad__sub-en">
                          Consulting firm for the design and construction of
                          private mansions
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── 作品集 / 伙伴圈：横向滑片 + 光标 ── */}
                {isSlides && (
                  <div className="m-service__media m-service__media--slides">
                    <div className="m-slides">
                      {s.imgs.map((img, i) => {
                        const cls =
                          i < slideActive
                            ? "is-prev"
                            : i === slideActive
                            ? "is-active"
                            : "is-next";
                        return (
                          <img
                            key={i}
                            className={`m-slides__img ${cls}`}
                            src={assetUrl(img)}
                            alt=""
                          />
                        );
                      })}
                      {/* 光标 + 光圈指示器 */}
                      {fxActive === 0 && (
                        <div className="m-slides__fx m-slides__fx--cursor-travel">
                          <div className="m-slides__fx-travel">
                            <div className="m-slides__fx-cursor-pin">
                              <span
                                className="m-slides__fx-cursor"
                                style={bgImageStyle(imgDemoCursor)}
                              />
                            </div>
                          </div>
                          <div className="m-slides__fx-focus m-slides__fx-focus--end">
                            <span className="m-slides__fx-halo" />
                            <span className="m-slides__fx-core" />
                          </div>
                        </div>
                      )}
                      {fxActive === 1 && (
                        <div className="m-slides__fx m-slides__fx--cursor-static">
                          <span className="m-slides__fx-cursor m-slides__fx-cursor--static">
                            <span
                              className="m-slides__fx-cursor-icon"
                              style={bgImageStyle(imgDemoCursor)}
                            />
                          </span>
                          <div className="m-slides__fx-focus m-slides__fx-focus--static">
                            <span className="m-slides__fx-halo" />
                            <span className="m-slides__fx-core" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <h3>{s.title}</h3>
                <p className="m-service__desc">
                  {s.desc.map((line, lineIdx) => (
                    <span key={lineIdx} className="m-service__line">
                      {line}
                    </span>
                  ))}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="m-cases" id="m-cases">
        <div className="m-sec-head">
          <span className="m-eyebrow">服务案例</span>
          <div className="m-sec-title">案例展示</div>
          {/* <p>3500+团队/企业已将不觉融入日常办公，让每一次分享、每一次链接都产生价值</p> */}
          {/* <a className="m-cases__more" href="#" onClick={(e) => e.preventDefault()}>
            查看更多
          </a> */}
        </div>
        <div className="m-cases__stage">
          <div
            className="m-cases__img"
            onTouchStart={handleCaseTouchStart}
            onTouchEnd={handleCaseTouchEnd}
            style={{ "--case-idx": caseActive }}
          >
            <div className="m-cases__img-track">
              {CASE_IMAGES.map((src, i) => (
                <div
                  key={i}
                  className="m-cases__img-slide"
                  style={bgImageStyle(src)}
                />
              ))}
            </div>
          </div>
          <div className="m-cases__dots">
            {CASE_IMAGES.map((_, i) => (
              <button
                key={i}
                type="button"
                className={i === caseActive ? "is-active" : ""}
                aria-label={`案例 ${i + 1}`}
                onClick={() => goCase(i)}
              />
            ))}
          </div>
        </div>
        <div className="m-cases__brands">
          <div className="m-cases__brands-track">
            {[...SCROLL_BRAND_IMAGES, ...SCROLL_BRAND_IMAGES].map((src, i) => (
              <div key={`m-brand-${i}`} className="m-cases__brand-item">
                <img src={assetUrl(src)} alt="" draggable={false} />
              </div>
            ))}
          </div>
        </div>
        <div className="m-cases__contact">
          <a
            href="https://v.wjx.cn/vm/PD6aaEY.aspx#"
            className="m-btn m-btn--primary m-btn--white"
            target="_blank"
            rel="noopener noreferrer"
          >
            联系我们
          </a>
        </div>
      </section>
      </>
      )}

      {!hideFooter && (
      <footer className="m-footer">
        <a href="#" className="m-footer__brand">
          <img src={assetUrl(imgBujueLogoWhite)} alt="不觉" />
        </a>
        <div className="m-footer__cols">
          <div className="m-footer__col">
            <h4>友情链接</h4>
            <ul>
              <li>
                <a
                  href="https://www.shejidedao.com/?a=bujue"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  设计得到
                </a>
              </li>
              <li>
                <a
                  href="https://www.archebase.co.jp/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ARCHBASE建材供应链
                </a>
              </li>
            </ul>
          </div>
          <div className="m-footer__col">
            <h4>支持帮助</h4>
            <ul>
              <li>
                <a
                  href={OFFICIAL_SITE_INSTRUCTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  操作说明
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleAppDownload(); }}
                >
                  下载APP
                </a>
              </li>
              <li>
                <a
                  href={`/agreement.html?fileUrl=${protocol.privacy}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  隐私政策
                </a>
              </li>
              <li>
                <a
                  href={`/agreement.html?fileUrl=${protocol.teamservice}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  服务条款
                </a>
              </li>
              {/* <li>
                <a
                  href="https://bujue.art/material_square.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  找材料
                </a>
              </li> */}
            </ul>
          </div>
          {/* <div className="m-footer__col">
            <h4>友情链接</h4>
            <ul>
              <li>
                <a
                  href="https://www.shejidedao.com/?a=bujue"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  设计得到
                </a>
              </li>
              <li>
                <a
                  href="https://panbadou.panzitech.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  项目管理工具
                </a>
              </li>
            </ul>
          </div> */}
          <div className="m-footer__col">
            <h4>关于我们</h4>
            <ul>
                <li>
                  <a
                    href="https://mp.weixin.qq.com/s?__biz=Mzk0ODYxNTM4Ng==&mid=2247483956&idx=1&sn=fc2eb3617ff02514fdeaa33f21b08542&chksm=c291a1df30a5152e1de626808116c5ac2e8ef2effce9339ae2e73207c2f4d329c42723400c98&mpshare=1&scene=1&srcid=0717gAZlU6igrtnd9MBiZq6X&sharer_shareinfo=f2cc040ae2937be0aadb2f70d0ab82ba&sharer_shareinfo_first=f2cc040ae2937be0aadb2f70d0ab82ba#rd"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    关于我们
                  </a>
                </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleIntroDownload(); }}
                >
                  下载介绍
                </a>
              </li>
            </ul>
          </div>
          <div className="m-footer__col">
            <h4>联系我们</h4>
            <ul>
              <li>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); setContactVisible(true); }}
                >
                  联系客服
                </a>
              </li>
              <li>
                <a
                  href="https://v.wjx.cn/vm/PD6aaEY.aspx#"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  企业咨询
                </a>
              </li>
              <li>
                <a className="footer-col__static">
                  邮箱：bujuekefu@bujue.art
                </a>
              </li>
              <li>
                <a className="footer-col__static">
                  电话：400-189-9196
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="m-footer__bottom">
          <span>© 2026 杭州不觉科技有限公司</span>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
          >
            浙ICP备17033477号-5
          </a>
        </div>
      </footer>
      )}
    </div>

      {contactVisible && (
        <div className="ow-modal-overlay" onClick={() => setContactVisible(false)}>
          <div className="ow-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ow-modal-close" type="button" onClick={() => setContactVisible(false)}>×</button>
            <h3 className="ow-modal-title">联系我们</h3>
            <div className="ow-modal-body">
              <img src={assetUrl(imgWxKf)} className="ow-modal-qr" alt="客服二维码" />
              <p className="ow-modal-text">扫码添加客服微信</p>
              <p className="ow-modal-sub">你也可以拨打400-189-9196</p>
            </div>
          </div>
        </div>
      )}

      {appModalVisible && (
        <div className="ow-modal-overlay" onClick={() => setAppModalVisible(false)}>
          <div className="ow-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ow-modal-close" type="button" onClick={() => setAppModalVisible(false)}>×</button>
            <h3 className="ow-modal-title">获取APP</h3>
            <div className="ow-modal-body">
              <QRCodeCanvas value={getUrl() + "down_app.html"} size={170} fgColor="#000000" />
              <p className="ow-modal-text">扫一扫下载APP</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
